import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isTrackingAllowed } from "@/lib/analytics";

const DRAFT_SESSION_KEY = "inquiry-draft-session-id";
const DRAFT_TOKEN_KEY = "inquiry-draft-token";

const randomString = (len: number) => {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("").slice(0, len);
};

const getDraftSessionId = (): string => {
  let sid = sessionStorage.getItem(DRAFT_SESSION_KEY);
  if (!sid) {
    sid = `draft_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    sessionStorage.setItem(DRAFT_SESSION_KEY, sid);
  }
  return sid;
};

const getDraftToken = (): string => {
  let token = sessionStorage.getItem(DRAFT_TOKEN_KEY);
  if (!token) {
    token = randomString(32);
    sessionStorage.setItem(DRAFT_TOKEN_KEY, token);
  }
  return token;
};

// Modul-lokale Sperre: nach erfolgreichem Submit dürfen für diese Session
// keine weiteren (verspäteten) Upserts mehr in die DB geschrieben werden.
const convertedSessions = new Set<string>();

export interface InquiryDraftPayload {
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  customer_company?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  vehicle_id?: string | null;
  vehicle_label?: string | null;
  vehicle_type?: string | null;
  temperature_requirement?: string | null;
  cargo_description?: string | null;
  delivery_enabled?: boolean | null;
  delivery_address?: string | null;
  extra_km?: number | null;
  source_page?: string | null;
  form_data?: Record<string, unknown> | null;
}

/**
 * Speichert teilausgefüllte Formulare nach 2s Inaktivität.
 * - Nur mit Cookie-Consent (analytics)
 * - Nur wenn E-Mail ODER Telefon ausgefüllt sind
 * - Updates sind durch einen pro-Session-Token geschützt (server-side RPC)
 */
export const useInquiryDraft = (payload: InquiryDraftPayload, enabled: boolean = true) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSerializedRef = useRef<string>("");
  const payloadRef = useRef<InquiryDraftPayload>(payload);
  payloadRef.current = payload;

  // Nur auf tatsächliche Wertänderungen reagieren – nicht auf jede neue Objekt-Referenz.
  // Das entkoppelt Draft-Save von jedem Tastendruck und verhindert Freeze bei
  // schneller Eingabe (z.B. Telefonnummer) auf schwachen Mobilgeräten.
  const serialized = JSON.stringify(payload);

  useEffect(() => {
    if (!enabled) return;
    if (!isTrackingAllowed()) return;

    const current = payloadRef.current;
    const hasContact =
      (current.customer_email && current.customer_email.includes("@")) ||
      (current.customer_phone && current.customer_phone.replace(/\D/g, "").length >= 6);
    if (!hasContact) return;

    if (serialized === lastSerializedRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const p_session_id = getDraftSessionId();
      if (convertedSessions.has(p_session_id)) return;
      lastSerializedRef.current = serialized;
      const p_token = getDraftToken();
      try {
        const latest = payloadRef.current;
        const p_payload = {
          ...latest,
          source_page: latest.source_page ?? window.location.pathname,
        };
        await supabase.rpc("upsert_inquiry_draft" as never, {
          p_session_id,
          p_token,
          p_payload: p_payload as never,
        } as never);
      } catch (err) {
        console.warn("draft save failed", err);
      }
    }, 2000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [serialized, enabled]);
};

/** Nach erfolgreichem Submit aufrufen – markiert den Draft als konvertiert. */
export const clearInquiryDraft = async () => {
  const sid = sessionStorage.getItem(DRAFT_SESSION_KEY);
  const token = sessionStorage.getItem(DRAFT_TOKEN_KEY);
  if (sid) {
    // Sofort sperren – verhindert, dass ein bereits laufender 2s-Timer
    // diesen Draft NACH dem Submit erneut anlegt.
    convertedSessions.add(sid);
  }
  if (sid && token) {
    try {
      await supabase.rpc("mark_inquiry_draft_converted" as never, {
        p_session_id: sid,
        p_token: token,
      } as never);
    } catch {
      /* noop */
    }
  }
  sessionStorage.removeItem(DRAFT_SESSION_KEY);
  sessionStorage.removeItem(DRAFT_TOKEN_KEY);
};
